import {
  anApiV1Cycle,
  aCyclePricing,
  aPricingInfo,
  aProductInfo,
  aQuotaFeature,
  anInterval,
} from '@wix/ambassador-premium-store/builders';
import {
  CyclePricing,
  Feature,
  IntervalUnit,
  SalePricing,
} from '@wix/ambassador-premium-store/types';

export const mockedCyclePricing = ({
  price,
  priceWithTax,
  monthsPerCycle,
  intervalCount,
  intervalUnit = IntervalUnit.YEAR,
}: {
  price: number;
  priceWithTax: number;
  monthsPerCycle: number;
  intervalCount: number;
  intervalUnit?: IntervalUnit;
}) =>
  aCyclePricing()
    .withCycle(
      anApiV1Cycle()
        .withInterval(
          anInterval().withCount(intervalCount).withUnit(intervalUnit).build(),
        )
        .build(),
    )
    .withPrice(price)
    .withPriceWithTax(priceWithTax)
    .withMonthlyPrice(
      intervalUnit === IntervalUnit.YEAR ? price / (12 * intervalCount) : price,
    )
    .withMonthsPerCycle(monthsPerCycle)
    .build();

export const aPurchasableCycle = ({
  unit,
  count,
}: {
  unit: IntervalUnit;
  count: number;
}) =>
  anApiV1Cycle()
    .withInterval(anInterval().withUnit(unit).withCount(count).build())
    .build();

const basePurchasableCycles = [
  aPurchasableCycle({ unit: IntervalUnit.YEAR, count: 2 }),
  aPurchasableCycle({ unit: IntervalUnit.YEAR, count: 3 }),
  aPurchasableCycle({ unit: IntervalUnit.YEAR, count: 1 }),
  aPurchasableCycle({ unit: IntervalUnit.MONTH, count: 1 }),
];

export const aDynamoProduct = ({
  name,
  productId,
  catalogPricing,
  sbsProductIdPoc = '',
  productTypeId = '',
  productFamilyId = '',
  features = [],
  salePricing = {},
}: {
  name: string;
  productId: string;
  catalogPricing: CyclePricing[];
  sbsProductIdPoc?: string;
  productTypeId?: string;
  productFamilyId?: string;
  features?: Feature[];
  salePricing?: SalePricing;
}) => {
  return (
    aProductInfo()
      .withName(name)
      .withProductFamilyId(productFamilyId)
      .withProductTypeId(productTypeId)
      .withProductId(productId)
      .withPurchasableCycles(basePurchasableCycles)
      .withDowngradePurchasableCycles([])
      // cyclePricing under PricingInfo is deprecated
      .withPricingInfo(
        aPricingInfo()
          .withCatalogPricing(catalogPricing)
          .withSalePricing(salePricing)
          .build(),
      )
      .withSbsProductIdPoc(sbsProductIdPoc)
      .withFeatures(features) // the offering is returned with a list of delivered features which are not used on client side.
      .build()
  );
};

interface DynamoFeatureBuilderParams {
  name: string;
  tooltip: string;
  group: string;
  isBoolean: boolean;
  priority?: number;
  quotaFeatureLimit?: number;
  value?: string;
  falseValue?: string;
}

export const aDynamoFeature = ({
  name,
  tooltip,
  isBoolean,
  group,
  priority,
  quotaFeatureLimit,
  value,
  falseValue,
}: DynamoFeatureBuilderParams) => {
  const feature: Feature = {
    name,
    displayProperties: {
      tooltip,
      group,
    },
  };
  if (priority) {
    feature!.displayProperties!.priority = `${priority}`;
  }
  if (value) {
    feature!.displayProperties!.value = value;
  }
  if (falseValue) {
    feature!.displayProperties!.falseValue = falseValue;
  }
  if (isBoolean) {
    feature.booleanFeature = {};
  } else {
    feature.quotaFeature = aQuotaFeature().build();
    feature.quotaFeature.limit = quotaFeatureLimit;
  }

  return feature;
};
